import React, {
	useRef,
	useEffect,
	useMemo,
	useState,
	useCallback
} from 'react';
import { useRequestData } from 'wsm-common-data';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { galleryTrackingEvents } from '../../utilities/tracking';
import './thumbnails.scss';

const SLIDE_TO_SHOW = 6;
function Thumbnails({
	carouselIndex,
	isMobile,
	setCarouselIndex,
	modalSliderRef,
	imgTitle
}) {
	const { imagesToDisplay = [] } = useSelector((state) => state.media);
	const { windowId, widgetName } = useRequestData();
	const isInitialMount = useRef(true);
	const containerRef = useRef(null);
	const slider = useRef();
	const [slidesToShow, setSlidesToShow] = useState(null);
	const imagesCount = imagesToDisplay.length;

	const settings = useMemo(
		() => ({
			infinite: false,
			slidesToScroll: 1,
			vertical: !isMobile,
			lazyLoad: false,
			focusOnSelect: false,
			variableWidth: isMobile,
			initialSlide: carouselIndex
		}),
		[carouselIndex, isMobile]
	);

	const mergedSettings = {
		...settings,
		...(slidesToShow && { slidesToShow })
	};

	const getSlideToDisplay = () => {
		if (!containerRef.current) {
			return SLIDE_TO_SHOW;
		}

		const thumbWrapperHeight = document.querySelector(
			'#gallery-modal-thumbnail-wrapper'
		).clientHeight;
		const slideHeight =
			containerRef.current.querySelector('.slick-slide').clientHeight;

		const _slidesToShow = thumbWrapperHeight / slideHeight;

		return _slidesToShow > 3
			? Math.floor(_slidesToShow)
			: Math.round(_slidesToShow);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateSlidesToShow = useCallback(
		window._.debounce(() => {
			const calculatedSlidesToShow = getSlideToDisplay();
			const limitedSlidesToShow = Math.min(
				calculatedSlidesToShow,
				SLIDE_TO_SHOW,
				imagesCount
			);

			setSlidesToShow(limitedSlidesToShow);
		}, 100),
		[]
	);

	useEffect(() => {
		// sync with carousel
		if (isInitialMount.current) {
			isInitialMount.current = false;
		} else {
			slider.current.slickGoTo(carouselIndex);
		}
	}, [carouselIndex, isMobile]);

	const onClickSlide = (index) => {
		galleryTrackingEvents.clickThumbnail(widgetName, windowId, {
			currentItem: carouselIndex + 1,
			totalItems: imagesCount
		});
		setCarouselIndex(index);
		modalSliderRef.current.slickGoTo(index);
	};

	return (
		<div
			ref={containerRef}
			className={`slider-container thumbnails-container ${
				isMobile && carouselIndex === 0 ? 'ml-4' : ''
			}`}
		>
			<Slider ref={slider} {...mergedSettings}>
				{imagesToDisplay.map((image, index) => {
					return isMobile ? (
						<button
							onClick={() => onClickSlide(index)}
							className="btn-unstyled"
							type="button"
							key={image.src}
						>
							<Image
								alt={`${
									index + 1
								} per ${imagesCount} vehicle thumbnail images`}
								key={`thumb-${image.thumbnail}`}
								className={`slider-thumbnail ${
									index === carouselIndex ? 'active' : ''
								} slider-thumbnail--${
									isMobile ? 'mobile mr-4' : 'desktop'
								}`}
								src={image.thumbnail}
								title={imgTitle}
							/>
						</button>
					) : (
						<button
							onClick={() => onClickSlide(index)}
							type="button"
							className="btn-unstyled mb-sm-4"
							key={image.src}
						>
							<Image
								alt={`${
									index + 1
								} per ${imagesCount} vehicle thumbnail images`}
								key={`thumb-${image.thumbnail}`}
								className={`slider-thumbnail w-100 ${
									index === carouselIndex ? 'active' : ''
								} slider-thumbnail--${
									isMobile ? 'mobile' : 'desktop'
								}
								`}
								src={image.thumbnail}
								onLoad={updateSlidesToShow}
								title={imgTitle}
							/>
						</button>
					);
				})}
			</Slider>
		</div>
	);
}

Thumbnails.propTypes = {
	isMobile: PropTypes.bool,
	carouselIndex: PropTypes.number.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	modalSliderRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	]),
	imgTitle: PropTypes.string
};

export default Thumbnails;
